import React from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { 
  CreditCardIcon, 
  ShieldCheckIcon, 
  LockClosedIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../../context/AuthContext';
import { usePaymentMethods } from '../../../hooks/usePaymentMethods';
import PaymentMethodManager from '../components/PaymentMethods/PaymentMethodManager';
import { 
  getCardExpiryStatus
} from '../../../utils/paymentUtils';

const PaymentMethodsPage: React.FC = () => {
  const { auth } = useAuth();
  const { paymentMethods, stripeInitialized } = usePaymentMethods();
  const expiredCards = paymentMethods.filter(pm => 
    getCardExpiryStatus(pm.expMonth, pm.expYear) === 'expired'
  ).length;
  const expiringSoonCards = paymentMethods.filter(pm => 
    getCardExpiryStatus(pm.expMonth, pm.expYear) === 'expiring_soon'
  ).length;
  const defaultMethod = paymentMethods.find(pm => pm.isDefault);

  if (!auth.isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl p-8 shadow-lg max-w-md mx-auto"
          >
            <CreditCardIcon className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Acceso Requerido
            </h1>
            <p className="text-gray-600 mb-6">
              Debes iniciar sesión para gestionar tus métodos de pago
            </p>
            <Link
              to="/login"
              className="inline-block px-6 py-3 bg-yellow-400 hover:bg-yellow-500 
                       text-black rounded-full font-medium transition-colors"
            >
              Iniciar Sesión
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }
  
  return (
    <>
      <Helmet>
        <title>Métodos de Pago - Jarad</title>
        <meta name="description" content="Gestiona tus tarjetas y métodos de pago guardados en Jarad" />
      </Helmet>

      <div className="bg-gray-50 min-h-screen py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <Link to="/account" className="text-sm font-medium text-black hover:text-gray-700">
              ← Volver a mi cuenta
            </Link>
          </div>

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-8"
          >
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Métodos de Pago
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Gestiona tus tarjetas guardadas para hacer tus compras más rápidas y seguras
            </p>
          </motion.div>

          {/* Payment System Status */}
          {!stripeInitialized && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4 mb-6"
            >
              <div className="flex items-center gap-3">
                <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600" />
                <p className="text-yellow-800 text-sm">
                  Inicializando sistema de pagos...
                </p>
              </div>
            </motion.div>
          )}

          {/* Payment Methods Statistics */}
          {paymentMethods.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8"
            >
              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Tarjetas Guardadas</p>
                    <p className="text-2xl font-bold text-gray-900">{paymentMethods.length}</p>
                  </div>
                  <CreditCardIcon className="w-8 h-8 text-blue-500" />
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Tarjeta por Defecto</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {defaultMethod ? `•••• ${defaultMethod.last4}` : 'Ninguna'}
                    </p>
                  </div>
                  <CheckCircleIcon className="w-8 h-8 text-green-500" />
                </div>
              </div>

              <div className="bg-white rounded-2xl p-6 shadow-sm">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Estado</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {expiredCards > 0 || expiringSoonCards > 0 ? (
                        <span className="text-yellow-600">Revisar</span>
                      ) : (
                        <span className="text-green-600">Todo OK</span>
                      )}
                    </p>
                  </div>
                  <ShieldCheckIcon className="w-8 h-8 text-yellow-500" />
                </div>
              </div>
            </motion.div>
          )}

          {/* Alerts for expired/expiring cards */}
          <AnimatePresence>
            {(expiredCards > 0 || expiringSoonCards > 0) && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mb-6"
              >
                {expiredCards > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-4">
                    <div className="flex items-center gap-3">
                      <ExclamationTriangleIcon className="w-5 h-5 text-red-600" />
                      <div>
                        <p className="text-red-800 font-medium">
                          {expiredCards} tarjeta{expiredCards > 1 ? 's' : ''} expirada{expiredCards > 1 ? 's' : ''}
                        </p>
                        <p className="text-red-600 text-sm">
                          Actualiza o elimina las tarjetas expiradas para evitar problemas en tus compras.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {expiringSoonCards > 0 && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-4">
                    <div className="flex items-center gap-3">
                      <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600" />
                      <div>
                        <p className="text-yellow-800 font-medium">
                          {expiringSoonCards} tarjeta{expiringSoonCards > 1 ? 's' : ''} expira{expiringSoonCards > 1 ? 'n' : ''} pronto
                        </p>
                        <p className="text-yellow-600 text-sm">
                          Considera actualizar estas tarjetas antes de que expiren.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Payment Method Manager */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl shadow-sm overflow-hidden"
          >
            <div className="p-6">
              <PaymentMethodManager
                mode="management"
                showTitle={false}
              />
            </div>
          </motion.div>

          {/* Security Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100"
          >
            <div className="flex items-center gap-3 mb-6">
              <LockClosedIcon className="w-6 h-6 text-blue-600" />
              <h3 className="text-xl font-semibold text-gray-900">
                Seguridad y Protección
              </h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start gap-3">
                <ShieldCheckIcon className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">
                    Cifrado SSL/TLS
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Toda la información se transmite con cifrado de nivel bancario.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <ShieldCheckIcon className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">
                    Procesado por Stripe
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Utilizamos Stripe, líder mundial en pagos seguros.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <ShieldCheckIcon className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">
                    Cumplimiento PCI DSS
                  </h4>
                  <p className="text-gray-600 text-sm">
                    Cumplimos con los más altos estándares de seguridad.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <ShieldCheckIcon className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-gray-900 mb-1">
                    Sin almacenamiento local
                  </h4>
                  <p className="text-gray-600 text-sm">
                    No guardamos datos completos de tarjetas en nuestros servidores.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default PaymentMethodsPage; 